import React, { useMemo } from 'react';
import { mapValues, groupBy } from 'lodash';
import { useHistory, useLocation } from 'react-router';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import SearchBar from '../../../../../../shared/designSystem/components/SearchBar';
import { formatNumber, formatWholeNumber } from '../../../../../../../core/utils/campaigns';

// =============================================================================
// BasketItemsTable — V1 Orders browser in the right-hand pane, migrated to the
// shared v2 DataTable primitive + SearchBar. Mirrors V2's BasketOrdersSection
// structure: one column (Order ID), expandable row shows the per-order line
// items with quantity / price / total.
//
// V1 data shape preserved: `aggregatedData` arrives as { [ORDER_ID]: items[] }
// with UPPERCASE keys; we groupBy NAME inside each order (same as before) so
// duplicate line items consolidate. URL-param-driven `orderId` search is
// unchanged — pressing enter writes `?orderId=` via history.push.
// =============================================================================

const orderIdSort = (rowA, rowB) =>
  String(rowA.original.orderId || '').localeCompare(String(rowB.original.orderId || ''));

const BasketItemsTable = ({
  orderIdVal,
  setOrderIdVal,
  aggregatedData,
  currentPage,
  setCurrentPage,
}) => {
  const location = useLocation();
  const history = useHistory();

  // Transform V1 aggregatedData -> list of { orderId, items: [...] } that
  // DataTable can consume directly. groupBy NAME inside each order matches
  // the previous implementation so duplicate line items consolidate into a
  // single row with summed QUANTITY and TOTAL.
  const orders = useMemo(() => {
    if (!aggregatedData) return [];
    return Object.entries(aggregatedData).map(([orderId, items]) => {
      const byName = mapValues(groupBy(items, 'NAME'), (list) => {
        let quantity = 0;
        let total = 0;
        list.forEach((obj) => {
          quantity += obj.QUANTITY;
          total += obj.PRICE * obj.QUANTITY;
        });
        return {
          NAME: list[0].NAME,
          PRICE: list[0].PRICE,
          QUANTITY: quantity,
          TOTAL: total,
        };
      });
      return {
        orderId,
        items: Object.values(byName),
      };
    });
  }, [aggregatedData]);

  const columns = [
    {
      id: 'orderId',
      accessorKey: 'orderId',
      header: 'Order ID',
      sortable: true,
      sortingFn: orderIdSort,
      meta: { align: 'left', width: 200 },
      cell: ({ getValue, row }) => {
        const id = getValue() || '—';
        const count = (row.original.items || []).length;
        return (
          <div className="campaign-overview__basket-orders-row">
            <span className="campaign-overview__creatives-name">{id}</span>
            {count > 0 && (
              <span className="campaign-overview__basket-orders-count">
                {formatWholeNumber(count)} item{count === 1 ? '' : 's'}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const renderExpandedRow = (record) => {
    const items = record.items || [];
    if (items.length === 0) {
      return (
        <div className="campaign-overview__basket-items-empty">
          No items in this order.
        </div>
      );
    }
    return (
      <ul className="campaign-overview__basket-items-list">
        {items.map((item, i) => (
          <li key={i} className="campaign-overview__basket-items-line">
            <span className="campaign-overview__basket-items-line-idx">{i + 1}.</span>
            <div className="campaign-overview__basket-items-line-body">
              <div className="campaign-overview__basket-items-line-name">{item.NAME}</div>
              <div className="campaign-overview__basket-items-line-meta">
                <span>Qty <strong>{formatWholeNumber(item.QUANTITY)}</strong></span>
                <span>Price <strong>${formatNumber(item.PRICE)}</strong></span>
                <span>Total <strong>${formatNumber(item.TOTAL)}</strong></span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderMobileCard = (record) => {
    const count = (record.items || []).length;
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <div className="campaign-overview__creatives-card-name">
            <span>{record.orderId || '—'}</span>
          </div>
        </div>
        <div className="campaign-overview__basket-orders-row">
          <span className="campaign-overview__basket-orders-count">
            {formatWholeNumber(count)} item{count === 1 ? '' : 's'}
          </span>
        </div>
      </>
    );
  };

  // URL-param protocol preserved: onSearch writes `?orderId=` (fires on
  // SearchBar's 300ms debounce). Keystrokes update the local orderIdVal so
  // the controlled input stays responsive.
  const handleSearch = (value) => {
    const params = new URLSearchParams(location.search);
    params.set('orderId', value || '');
    history.push(`${location.pathname}?${params.toString()}`);
  };

  const handleInputChange = (e) => {
    setOrderIdVal(e.target.value);
  };

  const handlePaginationChange = ({ pageIndex }) => {
    if (setCurrentPage) setCurrentPage(pageIndex + 1);
  };

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">Orders</div>
        <SearchBar
          placeholder="Search by order ID..."
          value={orderIdVal || ''}
          onChange={handleInputChange}
          onSearch={handleSearch}
          className="campaign-overview__creatives-search"
        />
      </header>

      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={orders}
            columns={columns}
            pageSize={10}
            pageSizeOptions={[10, 20, 50, 100]}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            pageIndex={(currentPage || 1) - 1}
            onPaginationChange={handlePaginationChange}
            expandable={{
              isRowExpandable: (record) => (record.items || []).length > 0,
              renderExpandedRow,
            }}
            emptyState="No orders match the current filter."
          />
        </div>
      </div>
    </section>
  );
};

export default BasketItemsTable;
